import { motion } from 'framer-motion';
import { Trophy, Award, Star, Target, Zap, Rocket, Radio, Mic2 } from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';

export const AchievementsWindow = () => {
  const achievements = [
    {
      icon: Rocket,
      title: 'Founder & CEO',
      description: 'Launched Pinnacle Event Organizing, leading youth-driven large-scale events and creative marketing.',
      date: 'July 2024',
      category: 'Entrepreneurship',
      color: 'text-primary'
    },
    {
      icon: Trophy,
      title: 'Technical Director',
      description: 'Appointed to the SISJ Student Council to oversee all technical aspects of school activities and production.',
      date: 'April 2023',
      category: 'Leadership',
      color: 'text-accent'
    },
    {
      icon: Radio,
      title: 'Radio Club President',
      description: 'Transformed the SISJ Radio Club into a vibrant media platform with live student-led broadcasts.',
      date: '2024',
      category: 'Media',
      color: 'text-success'
    },
    {
      icon: Zap,
      title: 'TEDx Event Ops Lead',
      description: 'Headed event operations for TEDxSISJ Youth, managing sound, lighting, and stage design to TED standards.',
      date: 'April 2025',
      category: 'Operations',
      color: 'text-primary'
    },
    {
      icon: Star,
      title: 'SIS Fest Tech Lead',
      description: 'Successfully managed technical execution for one of the school\'s largest cultural festivals.',
      date: 'Oct - Dec 2024',
      category: 'Event Mgmt',
      color: 'text-accent'
    },
    {
      icon: Mic2,
      title: 'Lead Sound Advisor',
      description: 'Recognized as the primary technical advisor for the SISJ Music Club, managing professional audio mixing.',
      date: '2024',
      category: 'Technical',
      color: 'text-success'
    }
  ];

  const timeline = [
    {
      year: '2025',
      events: [
        'Head of Event Operations for TEDxSISJ Youth',
        'Appointed Social Awareness Club Joint Secretary',
        'Senior Lead for technical mentor programs'
      ]
    },
    {
      year: '2024',
      events: [
        'Founded Pinnacle Event Organizing',
        'Technical Lead for SIS Fest \'24',
        'Elected Radio Club President',
        'Freelance Technical Support for AMS Productions'
      ]
    },
    {
      year: '2023',
      events: [
        'Appointed Technical Director of Student Council',
        'Started technical support for St. Michael\'s Church',
        'Transitioned to Grade 11 Commerce-Sociology focus'
      ]
    },
    {
      year: '2016',
      events: [
        'Joined Sharjah Indian School (Juwaiza)',
        'Began training in Sound & Light Engineering'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl font-bold glow-text mb-2">Achievements & Timeline</h1>
        <p className="text-muted-foreground">Key milestones in event leadership and technical direction</p>
      </motion.div>

      {/* Achievements Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {achievements.map((achievement, i) => (
          <motion.div
            key={achievement.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
          >
            <Card className="p-6 tech-border">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-xl bg-primary/10">
                  <achievement.icon className={`w-6 h-6 ${achievement.color}`} />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-foreground">{achievement.title}</h3>
                    <Badge variant="outline" className="text-xs">
                      {achievement.category}
                    </Badge>
                  </div>
                  <p className="text-sm text-foreground/70">{achievement.description}</p>
                  <div className="text-xs text-muted-foreground">{achievement.date}</div>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Timeline */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card className="p-6 tech-border">
          <h2 className="text-2xl font-bold text-primary mb-6">Growth Timeline</h2>
          <div className="space-y-6">
            {timeline.map((year, i) => (
              <motion.div
                key={year.year}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.7 + i * 0.1 }}
                className="flex gap-4"
              >
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center font-bold text-sm">
                    {year.year}
                  </div>
                  {i < timeline.length - 1 && (
                    <div className="w-px h-full bg-gradient-to-b from-primary/50 to-transparent mt-2" />
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <ul className="space-y-2">
                    {year.events.map((event, j) => (
                      <li key={j} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
                        <span className="text-sm text-foreground/80">{event}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <Card className="p-6 tech-border">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-primary mb-1">10+</div>
              <div className="text-xs text-muted-foreground">Major Events Led</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-accent mb-1">12+</div>
                <div className="text-xs text-muted-foreground">Coding Languages Mastered</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-success mb-1">10+</div>
              <div className="text-xs text-muted-foreground">Leadership Roles</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary mb-1">3+</div>
              <div className="text-xs text-muted-foreground">Years Technical Training</div>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};

export default AchievementsWindow;